import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiHome, FiPlus, FiFolder, FiMonitor, FiMenu, FiCloud,
  FiLayers, FiDollarSign, FiPlayCircle, FiSettings,
} from 'react-icons/fi';
import WorkspacePanel from './WorkspacePanel';
import { useSidebar } from '../../context/SidebarContext';
import { useDashboard } from '../../context/DashboardContext';
import { useAuth } from '../../context/AuthContext';

interface NavItem {
  label:    string;
  icon:     React.ReactNode;
  path:     string;
  onClick?: () => void;
  roles?:   string[];
}

const Sidebar = () => {
  const { t } = useTranslation('navigation');
  const navigate  = useNavigate();
  const location  = useLocation();
  const { isExpanded, setIsExpanded } = useSidebar();
  const { setCurrentView } = useDashboard();
  const { user } = useAuth();
  const [showWorkspaces, setShowWorkspaces] = useState(false);

  const go = (path: string) => {
    navigate(path);
    setIsExpanded(false);
  };

  const allNavItems: NavItem[] = [
    { label: t('menu.dashboard'),      icon: <FiHome size={18} />,       path: '/home' },
    { label: t('menu.gettingStarted'), icon: <FiPlus size={18} />,       path: '/getting-started' },
    { label: t('menu.projects'),       icon: <FiFolder size={18} />,     path: '/projects' },
    { label: 'Executions',             icon: <FiPlayCircle size={18} />, path: '/executions' },
    { label: 'Environnements',         icon: <FiSettings size={18} />,   path: '/environments' },
    { label: t('menu.templates'),      icon: <FiLayers size={18} />,     path: '/templates' },
    { label: t('menu.cloudServices'),  icon: <FiCloud size={18} />,      path: '/cloud-services' },
    { label: t('menu.finops'),         icon: <FiDollarSign size={18} />, path: '/finops',     roles: ['owner', 'admin'] },
    { label: t('menu.monitoring'),     icon: <FiMonitor size={18} />,    path: '/monitoring' },
  ];

  const userRole = user?.role || '';
  const navItems = allNavItems.filter(item =>
    !item.roles || item.roles.includes(userRole)
  );

  return (
    <div className="relative flex">
      <aside
        className={`${
          isExpanded ? 'w-64' : 'w-16'
        } bg-white min-h-screen shadow-md transition-all duration-300 flex flex-col overflow-visible`}
      >
        {/* Bouton Menu */}
        <div className="p-4 pb-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-gray-500 hover:text-blue-600 transition-colors"
            title={isExpanded ? 'Réduire' : 'Développer'}
          >
            <FiMenu size={22} />
          </button>
        </div>

        <nav className="flex flex-col px-2 pb-4 space-y-0.5 mt-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path !== '/home' && location.pathname.startsWith(item.path));

            return (
              <div key={item.path} className="relative group">
                <button
                  onClick={() => go(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  {isExpanded && <span className="whitespace-nowrap">{item.label}</span>}
                </button>

                {/* Tooltip au hover quand menu fermé */}
                {!isExpanded && (
                  <div className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50
                                  opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <div className="bg-gray-800 text-white text-xs px-2.5 py-1.5 rounded-lg shadow-lg whitespace-nowrap">
                      {item.label}
                    </div>
                    {/* Flèche */}
                    <div className="absolute right-full top-1/2 -translate-y-1/2
                                    border-4 border-transparent border-r-gray-800" />
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Panneau latéral pour projets */}
      {showWorkspaces && (
        <WorkspacePanel onClose={() => setShowWorkspaces(false)} />
      )}
    </div>
  );
};

export default Sidebar;
